import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Award, MapPin, FileText } from 'lucide-react';
import { reportesJugadoresService } from '../../services/reportesJugadoresService';
import type { ReporteJugador } from '../../types/reportesJugadores';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { translatePosition } from '../../utils/positionTranslations';

const ReporteJugadorDetailPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';
  const [reporte, setReporte] = useState<ReporteJugador | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (reportId) {
      loadReporte();
    }
  }, [reportId]);

  const loadReporte = async () => {
    if (!reportId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await reportesJugadoresService.getReporte(parseInt(reportId));

      if (response.success && response.data) {
        setReporte(response.data);
      } else {
        setError(response.error || 'Error al cargar el reporte');
      }
    } catch (err) {
      setError('Error de conexión al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getNotaColor = (nota: number | null | undefined): string => {
    if (!nota) return 'text-neutral-400';
    if (nota >= 8) return 'text-green-400';
    if (nota >= 6) return 'text-yellow-400';
    if (nota >= 4) return 'text-orange-400';
    return 'text-red-400';
  };

  const renderRating = (label: string, value: number | null | undefined) => {
    if (value === null || value === undefined) return null;

    return (
      <div className="bg-primary-700 p-4 rounded-xl">
        <p className="text-sm text-neutral-400 mb-2">{label}</p>
        <p className={`text-3xl font-bold ${getNotaColor(value)}`}>
          {value.toFixed(1)}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !reporte) {
    return (
      <div className="card text-center py-12">
        <FileText className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-neutral-100 mb-2">
          {error || 'Reporte no encontrado'}
        </h3>
        <button
          onClick={() => navigate('/sporting/reportes-jugadores')}
          className="btn-primary inline-flex items-center gap-2 mt-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la lista
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/sporting/reportes-jugadores')}
          className="p-2 text-neutral-400 hover:text-neutral-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <div>
          <h1 className="section-title">
            Reporte de <span className="text-accent-400">{reporte.player_name || 'Jugador'}</span>
          </h1>
          <p className="section-subtitle">
            Informe de scouting • {formatDate(reporte.fecha_informe)}
          </p>
        </div>
      </div>

      {/* Información Básica */}
      <div className="card">
        <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
          <User className="w-5 h-5" />
          Información del Jugador
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-neutral-400 mb-1">Nombre</p>
            <p className="text-lg font-semibold text-neutral-100">{reporte.player_name || 'N/A'}</p>
          </div>

          {reporte.posicion_principal && (
            <div>
              <p className="text-sm text-neutral-400 mb-1">Posición Principal</p>
              <p className="text-lg font-semibold text-neutral-100">{translatePosition(reporte.posicion_principal, currentLang)}</p>
            </div>
          )}

          {reporte.posicion_secundaria && (
            <div>
              <p className="text-sm text-neutral-400 mb-1">Posición Secundaria</p>
              <p className="text-lg font-semibold text-neutral-100">{translatePosition(reporte.posicion_secundaria, currentLang)}</p>
            </div>
          )}

          {reporte.pie_preferido && (
            <div>
              <p className="text-sm text-neutral-400 mb-1">Pie Preferido</p>
              <p className="text-lg font-semibold text-neutral-100">{reporte.pie_preferido}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-neutral-400 mb-1">Fecha del Informe</p>
            <p className="text-lg font-semibold text-neutral-100">{formatDate(reporte.fecha_informe)}</p>
          </div>

          {reporte.nombre_usuario && (
            <div>
              <p className="text-sm text-neutral-400 mb-1">Scout</p>
              <p className="text-lg font-semibold text-neutral-100">{reporte.nombre_usuario}</p>
            </div>
          )}
        </div>
      </div>

      {/* Información del Partido */}
      {(reporte.nombre_competicion || reporte.nombre_equipo_rival || reporte.fecha_partido) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Calendar className="w-5 h-5" />
            Información del Partido
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reporte.nombre_competicion && (
              <div>
                <p className="text-sm text-neutral-400 mb-1">Competición</p>
                <p className="text-lg font-semibold text-neutral-100">{reporte.nombre_competicion}</p>
              </div>
            )}

            {reporte.jornada && (
              <div>
                <p className="text-sm text-neutral-400 mb-1">Jornada</p>
                <p className="text-lg font-semibold text-neutral-100">{reporte.jornada}</p>
              </div>
            )}

            {reporte.nombre_equipo_rival && (
              <div>
                <p className="text-sm text-neutral-400 mb-1">Equipo Rival</p>
                <p className="text-lg font-semibold text-neutral-100">{reporte.nombre_equipo_rival}</p>
              </div>
            )}

            {reporte.lugar_partido && (
              <div>
                <p className="text-sm text-neutral-400 mb-1">Lugar</p>
                <p className="text-lg font-semibold text-neutral-100">{reporte.lugar_partido}</p>
              </div>
            )}

            {reporte.fecha_partido && (
              <div>
                <p className="text-sm text-neutral-400 mb-1">Fecha del Partido</p>
                <p className="text-lg font-semibold text-neutral-100">{formatDate(reporte.fecha_partido)}</p>
              </div>
            )}

            {reporte.minutos_jugados && (
              <div>
                <p className="text-sm text-neutral-400 mb-1">Minutos Jugados</p>
                <p className="text-lg font-semibold text-neutral-100">{reporte.minutos_jugados}'</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Valoración General */}
      {(reporte.nota_general || reporte.nota_ofensiva || reporte.nota_defensiva) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-accent-400" />
            Valoración General
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {renderRating('Nota General', reporte.nota_general)}
            {renderRating('Nota Ofensiva', reporte.nota_ofensiva)}
            {renderRating('Nota Ajustada Ofensiva', reporte.nota_ajustada_ofensiva)}
            {renderRating('Nota Defensiva', reporte.nota_defensiva)}
            {renderRating('Nota Ajustada Defensiva', reporte.nota_ajustada_defensiva)}
          </div>
        </div>
      )}

      {/* Aspectos Físicos */}
      {(reporte.nota_velocidad || reporte.nota_complexion || reporte.nota_potencia) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-blue-400" />
            Aspectos Físicos
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {renderRating('Velocidad', reporte.nota_velocidad)}
            {renderRating('Complexión', reporte.nota_complexion)}
            {renderRating('Potencia', reporte.nota_potencia)}
          </div>
        </div>
      )}

      {/* Aspectos Defensivos */}
      {(reporte.nota_duelos_defensivos || reporte.nota_tactica_defensiva || 
        reporte.nota_lectura_defensiva || reporte.nota_1x1_defensivo || 
        reporte.nota_juego_aereo_defensivo) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-red-400" />
            Aspectos Defensivos
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {renderRating('Duelos Defensivos', reporte.nota_duelos_defensivos)}
            {renderRating('Táctica Defensiva', reporte.nota_tactica_defensiva)}
            {renderRating('Lectura Defensiva', reporte.nota_lectura_defensiva)}
            {renderRating('1x1 Defensivo', reporte.nota_1x1_defensivo)}
            {renderRating('Juego Aéreo Defensivo', reporte.nota_juego_aereo_defensivo)}
          </div>
        </div>
      )}

      {/* Aspectos Ofensivos */}
      {(reporte.nota_juego_aereo_ofensivo || reporte.nota_marcaje_area || 
        reporte.nota_capacidad_ofensiva || reporte.nota_calidad_ofensiva) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-green-400" />
            Aspectos Ofensivos
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {renderRating('Juego Aéreo Ofensivo', reporte.nota_juego_aereo_ofensivo)}
            {renderRating('Marcaje en Área', reporte.nota_marcaje_area)}
            {renderRating('Capacidad Ofensiva', reporte.nota_capacidad_ofensiva)}
            {renderRating('Calidad Ofensiva', reporte.nota_calidad_ofensiva)}
          </div>
        </div>
      )}

      {/* Calidad Técnica */}
      {(reporte.nota_calidad_juego_corto || reporte.nota_calidad_juego_largo || 
        reporte.nota_saque_banda) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-yellow-400" />
            Calidad Técnica
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {renderRating('Juego Corto', reporte.nota_calidad_juego_corto)}
            {renderRating('Juego Largo', reporte.nota_calidad_juego_largo)}
            {renderRating('Saque de Banda', reporte.nota_saque_banda)}
          </div>
        </div>
      )}

      {/* Capacidades Generales */}
      {(reporte.nota_capacidad_juego || reporte.nota_capacidad_sacrificio) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-purple-400" />
            Capacidades Generales
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {renderRating('Capacidad de Juego', reporte.nota_capacidad_juego)}
            {renderRating('Capacidad de Sacrificio', reporte.nota_capacidad_sacrificio)}
          </div>
        </div>
      )}

      {/* Aspectos Mentales */}
      {(reporte.nota_comunicacion || reporte.nota_toma_decisiones || 
        reporte.nota_ruptura || reporte.nota_juego_de_espaldas) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <Award className="w-5 h-5 text-indigo-400" />
            Aspectos Mentales y Tácticos
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {renderRating('Comunicación', reporte.nota_comunicacion)}
            {renderRating('Toma de Decisiones', reporte.nota_toma_decisiones)}
            {renderRating('Ruptura', reporte.nota_ruptura)}
            {renderRating('Juego de Espaldas', reporte.nota_juego_de_espaldas)}
          </div>
        </div>
      )}

      {/* Observaciones */}
      {(reporte.perfil_estilo_juego || reporte.comentarios || reporte.notas_mentales) && (
        <div className="card">
          <h2 className="flex items-center gap-3 text-xl font-bold text-neutral-100 mb-6">
            <MapPin className="w-5 h-5" />
            Observaciones
          </h2>

          <div className="space-y-6">
            {reporte.perfil_estilo_juego && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-300 mb-2">Perfil y Estilo de Juego</h3>
                <p className="text-neutral-100 leading-relaxed">{reporte.perfil_estilo_juego}</p>
              </div>
            )}

            {reporte.comentarios && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-300 mb-2">Comentarios Generales</h3>
                <p className="text-neutral-100 leading-relaxed">{reporte.comentarios}</p>
              </div>
            )}

            {reporte.notas_mentales && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-300 mb-2">Notas Mentales</h3>
                <p className="text-neutral-100 leading-relaxed">{reporte.notas_mentales}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="flex justify-start">
        <button
          onClick={() => navigate('/sporting/reportes-jugadores')}
          className="btn-secondary flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la lista
        </button>
      </div>
    </div>
  );
};

export default ReporteJugadorDetailPage;
